import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useAppContext } from "../../../context";

const shellClass =
  "w-full rounded-3xl border border-white/60 bg-white p-8 shadow-2xl shadow-orange-100/60";
const inputClass =
  "w-full rounded-2xl border border-orange-100 bg-orange-50/50 px-4 py-3.5 text-sm text-gray-700 outline-none transition focus:border-orange-400 focus:bg-white";

const CustomerAuthForm = ({ mode = "login" }) => {
  const isLoginMode = mode === "login";
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginCustomer, register } = useAppContext();
  const redirectTo = location.state?.from?.pathname || "/profile";

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setError("");
  };

  const validateForm = () => {
    if (!form.email.trim() || !form.password.trim()) {
      return "Email and password are required.";
    }

    if (!isLoginMode) {
      if (!form.firstName.trim() || !form.lastName.trim()) {
        return "First name and last name are required.";
      }

      if (!form.phone.trim()) {
        return "Phone number is required.";
      }

      if (form.password.length < 6) {
        return "Password must be at least 6 characters.";
      }

      if (form.password !== form.confirmPassword) {
        return "Passwords do not match.";
      }
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    const result = isLoginMode
      ? await loginCustomer({ email: form.email, password: form.password })
      : await register({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          password: form.password,
        });

    if (!result.success) {
      setError(result.message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    navigate("/profile", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(251,146,60,0.28),_transparent_32%),linear-gradient(135deg,#fff7ed_0%,#fffbeb_48%,#fff1f2_100%)] px-4 py-14">
      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="hidden rounded-[2rem] bg-gradient-to-br from-orange-500 via-orange-400 to-red-500 p-10 text-white shadow-2xl shadow-orange-200/70 lg:block">
          <p className="mb-4 inline-flex rounded-full bg-white/15 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em]">
            Gamapa Foods
          </p>
          <h1 className="max-w-md text-4xl font-black leading-tight">
            {isLoginMode ? "Sign in and pick up where you left off." : "Create your account and start ordering faster."}
          </h1>
          <p className="mt-5 max-w-lg text-sm leading-7 text-orange-50">
            Save your details, keep your cart close, and unlock a profile page that stays available while your auth cookie is present.
          </p>
          <div className="mt-10 grid gap-4 text-sm">
            {[
              "Quick access to your profile and saved info",
              "Navbar switches to your first name after login",
              "Temporary cookie session for protected profile access",
            ].map((item) => (
              <div key={item} className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3">
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className={shellClass}>
          <div className="mb-8 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-orange-500">
              {isLoginMode ? "Welcome Back" : "New Account"}
            </p>
            <h2 className="mt-3 text-3xl font-black text-gray-900">
              {isLoginMode ? "Sign In" : "Register"}
            </h2>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            {!isLoginMode ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                    <UserRound size={14} />
                    First Name
                  </span>
                  <input className={inputClass} name="firstName" onChange={handleChange} value={form.firstName} />
                </label>
                <label className="block">
                  <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                    <UserRound size={14} />
                    Last Name
                  </span>
                  <input className={inputClass} name="lastName" onChange={handleChange} value={form.lastName} />
                </label>
              </div>
            ) : null}

            <label className="block">
              <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                <Mail size={14} />
                Email Address
              </span>
              <input className={inputClass} name="email" onChange={handleChange} type="email" value={form.email} />
            </label>

            {!isLoginMode ? (
              <label className="block">
                <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                  <Phone size={14} />
                  Phone Number
                </span>
                <input className={inputClass} name="phone" onChange={handleChange} value={form.phone} />
              </label>
            ) : null}

            <label className="block">
              <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                <LockKeyhole size={14} />
                Password
              </span>
              <input className={inputClass} name="password" onChange={handleChange} type="password" value={form.password} />
            </label>

            {!isLoginMode ? (
              <label className="block">
                <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                  <LockKeyhole size={14} />
                  Confirm Password
                </span>
                <input className={inputClass} name="confirmPassword" onChange={handleChange} type="password" value={form.confirmPassword} />
              </label>
            ) : null}

            {error ? (
              <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-500">
                {error}
              </div>
            ) : null}

            <button className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 px-4 py-4 text-sm font-black text-white shadow-lg shadow-orange-200 transition hover:-translate-y-0.5 disabled:opacity-60" disabled={submitting} type="submit">
              {submitting ? "Please wait..." : isLoginMode ? "Sign In" : "Create Account"}
              <ArrowRight size={16} />
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            {isLoginMode ? "Need an account?" : "Already registered?"}{" "}
            <Link className="font-bold text-orange-500 transition hover:text-orange-600" to={isLoginMode ? "/register" : "/login"}>
              {isLoginMode ? "Create one here" : "Go to sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const AdminLoginForm = ({ redirectPath = "/admin" }) => {
  const navigate = useNavigate();
  const { isAdminAuthenticated, isAdmin, loginAdmin } = useAppContext();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAdminAuthenticated && isAdmin) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAdmin, isAdminAuthenticated, navigate, redirectPath]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.email.trim() || !form.password.trim()) {
      setError("Admin email and password are required.");
      return;
    }

    setSubmitting(true);
    const result = await loginAdmin(form);
    if (!result.success) {
      setError(result.message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    navigate(redirectPath, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(17,24,39,0.24),_transparent_28%),linear-gradient(135deg,#fff7ed_0%,#f3f4f6_44%,#fff1f2_100%)] px-4 py-14">
      <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-[1fr_0.9fr]">
        <div className="hidden rounded-[2rem] bg-gray-900 p-10 text-white shadow-2xl shadow-gray-300/60 lg:block">
          <p className="mb-4 inline-flex rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em]">
            Admin Portal
          </p>
          <h1 className="max-w-md text-4xl font-black leading-tight">
            Separate admin access for store management.
          </h1>
          <p className="mt-5 max-w-lg text-sm leading-7 text-gray-300">
            This page is only for admin users. After login, protected admin routes like products, categories, orders, and order details become available.
          </p>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-gray-300">
            Default admin user: admin@gamapa.com / admin123
          </div>
        </div>

        <div className={shellClass}>
          <div className="mb-8 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-gray-500">
              Protected Admin Access
            </p>
            <h2 className="mt-3 text-3xl font-black text-gray-900">Admin Sign In</h2>
          </div>

          <form
            className="space-y-4"
            onSubmit={handleSubmit}
          >
            <label className="block">
              <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                <Mail size={14} />
                Admin Email
              </span>
              <input
                className={inputClass}
                name="email"
                onChange={(event) => {
                  setForm((current) => ({ ...current, email: event.target.value }));
                  setError("");
                }}
                type="email"
                value={form.email}
              />
            </label>

            <label className="block">
              <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                <LockKeyhole size={14} />
                Password
              </span>
              <input
                className={inputClass}
                name="password"
                onChange={(event) => {
                  setForm((current) => ({ ...current, password: event.target.value }));
                  setError("");
                }}
                type="password"
                value={form.password}
              />
            </label>

            {error ? (
              <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-500">
                {error}
              </div>
            ) : null}

            <button className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gray-900 px-4 py-4 text-sm font-black text-white shadow-lg shadow-gray-200 transition hover:bg-gray-800 disabled:opacity-60" disabled={submitting} type="submit">
              {submitting ? "Checking..." : "Admin Login"}
              <ShieldCheck size={16} />
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Protected access for administrators only.
          </p>
        </div>
      </div>
    </div>
  );
};

export const LoginPage = ({ mode, redirectPath }) =>
  mode === "admin" ? (
    <AdminLoginForm redirectPath={redirectPath} />
  ) : (
    <CustomerAuthForm mode="login" />
  );

export const RegisterPage = () => <CustomerAuthForm mode="register" />;
